import { createContext, useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import { destroyCookie, parseCookies } from 'nookies';

import { leapUrl } from '../../utils/leapUrl';
import { toUserProfile } from '../../utils/linkpassAdapters';

// The user endpoint. Overridable by env so a change of shape needs no deploy.
const USER_PATH = process.env.NEXT_PUBLIC_LEAP_USER_PATH || '/me/user';

const AccountStateContext = createContext();
const AccountDispatchContext = createContext();

function destroySideEffects() {
  const cookieOptions = { path: '/' };
  destroyCookie(null, 'accessId', cookieOptions);
  destroyCookie(null, 'authToken', cookieOptions);
  destroyCookie(null, 'accesstoken', cookieOptions);
  destroyCookie(null, 'userId', cookieOptions);
}

function accountReducer(state, action) {
  switch (action.type) {
    case 'LOADED': {
      return {
        ...state,
        loading: false,
      };
    }
    case 'UPDATE': {
      const accountData = isObject(action.value)
        ? { ...state.accountData, ...action.value }
        : action.value;
      const { authToken } = parseCookies();
      return {
        ...state,
        accountData,
        isLoggedIn: !isEmpty(accountData) && Boolean(authToken),
      };
    }
    /* isLoggedIn is only ever recomputed on UPDATE, so ending a session needs its own case. */
    case 'SESSION_ENDED': {
      return {
        ...state,
        accountData: {},
        profile: null,
        isLoggedIn: false,
      };
    }
    case 'UPDATE_PROFILE': {
      return {
        ...state,
        profile: action.value,
      };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function AccountProvider({ children }) {
  const initialState = {
    accountData: {},
    profile: null,
    isLoggedIn: false,
    loading: true,
  };
  const [state, dispatch] = useReducer(accountReducer, initialState);

  /*
   * Reads identity from the Linkpass user endpoint (see USER_PATH). The backend resolves
   * the member from the access token, so there is no accessId/userId to pass.
   *
   * NOTE: this response also carries `singpass` (NRIC and the MyInfo payload). Called from
   * the browser it lands in the client, so keep it out of Datadog RUM's captured payloads
   * and out of anything logged client side.
   */
  const loadLinkpassUser = async (authToken) => {
    try {
      const res = await fetch(leapUrl(USER_PATH), {
        headers: { Authorization: `Bearer ${authToken}`, Accept: 'application/json' },
      });

      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.error(`[${USER_PATH}] upstream returned`, res.status);
        logout({ silent: true });
        return;
      }

      const user = await res.json();
      /*
       * Nav renders "{fname} {lname}". The user endpoints differ in shape - one returns
       * firstName/lastName (camelCase), another a single `name` - so accept either rather
       * than depending on which one we are pointed at.
       */
      dispatch({
        type: 'UPDATE',
        value: { ...user, fname: user.firstName ?? user.name ?? '', lname: user.lastName ?? '' },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  };

  const refreshUserData = useCallback(async () => {
    const { authToken } = parseCookies();

    if (authToken) {
      await loadLinkpassUser(authToken);
    } else {
      dispatch({ type: 'SESSION_ENDED' });
    }
    dispatch({ type: 'LOADED' });
  }, []);

  function logout(options = {}) {
    const { redirect, silent } = options;

    /*
     * With Linkpass on there is no /v1/logout to call. Clearing our cookies is not enough
     * either - Auth0 keeps its own SSO session and would sign the user straight back in
     * without a prompt. /api/auth/logout clears the cookies and ends that session too.
     */
    destroySideEffects();
    if (silent) {
      return;
    }
    window.location = redirect
      ? `/api/auth/logout?redirect=${encodeURIComponent(redirect)}`
      : '/api/auth/logout';
  }

  async function fetchUserProfile() {
    /*
     * The user endpoint's fields are snake_case while the profile page reads camelCase,
     * so translate here rather than in each page.
     */
    const { authToken } = parseCookies();
    if (!authToken) {
      return;
    }
    try {
      const profileRes = await fetch(leapUrl(USER_PATH), {
        headers: { Authorization: `Bearer ${authToken}`, Accept: 'application/json' },
      });
      if (!profileRes.ok) {
        // eslint-disable-next-line no-console
        console.error(`[${USER_PATH}] profile fetch returned`, profileRes.status);
        return;
      }
      const u = await profileRes.json();
      dispatch({ type: 'UPDATE_PROFILE', value: toUserProfile(u) });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    }
  }

  useEffect(() => {
    refreshUserData();
  }, [refreshUserData]);

  /* Only a restore that follows a real session can tell an ended one from never having had one. */
  const hadSession = useRef(false);
  useEffect(() => {
    hadSession.current = state.isLoggedIn;
  }, [state.isLoggedIn]);

  /*
   * A back/forward cache restore hands the tree back exactly as it was, so no reducer runs
   * and isLoggedIn keeps the value it had before the member logged out - the nav goes on
   * offering Logout to someone who has already gone. This sits in the provider rather than
   * the route guard because public pages show member details too, and they have no guard.
   *
   * A session that ended needs a reload, not a state refresh: pages like the exchange detail
   * render the balance and member ids from getServerSideProps props, and nothing but a fresh
   * request re-evaluates those. The reload is confined to that case so ordinary back
   * navigation, signed in or out, stays instant.
   */
  useEffect(() => {
    const onPageShow = (event) => {
      if (!event.persisted) {
        return;
      }

      const { authToken } = parseCookies();
      if (!authToken && hadSession.current) {
        window.location.reload();
        return;
      }

      refreshUserData();
    };

    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, [refreshUserData]);

  return (
    <AccountStateContext.Provider
      value={{
        accountData: state.accountData,
        isLoggedIn: state.isLoggedIn,
        loading: state.loading,
        profile: state.profile,
        refreshUserData,
        logout,
        fetchUserProfile,
      }}
    >
      <AccountDispatchContext.Provider value={dispatch}>{children}</AccountDispatchContext.Provider>
    </AccountStateContext.Provider>
  );
}

function useAccountState() {
  const context = useContext(AccountStateContext);
  if (context === undefined) {
    throw new Error('useAccountState must be used within a AccountStateProvider');
  }
  return context;
}

function useAccountDispatch() {
  const context = useContext(AccountDispatchContext);
  if (context === undefined) {
    throw new Error('useAccountState must be used within a AccountDipatchProvider');
  }
  return context;
}

export { AccountProvider, useAccountState, useAccountDispatch };
